import { useMemo, useState, useEffect } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './BimloaFilter.module.scss';
import { AutocompleteItem } from './AutocompleteInput';
import MultiSelectDropDown, {
  MultiSelectOption,
  autocompleteItemToMultiSelectOption,
} from '@/components/school-management/MultiSelectDropDown/MultiSelectDropDown';
import { AutocompleteResponse } from '@/school-management/types/response/autocomplete';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';
import { autocompleteService } from '@/school-management/services/autocomplete';
import AppIcon from '../AppIcon/AppIcon';

interface AutocompleteMultiSelectFilterProps {
  value: AutocompleteItem[];
  onChange: (opts: AutocompleteItem[]) => void;
  isOpen: boolean;
  onOpen: () => void;
  autocompleteType?: 'teachers' | 'students' | 'groups' | 'grades' | 'class_code';
  usesAutocompletetartOptions?: boolean;
  label: string;
  searchPlaceholder?: string;
  organizationId?: string;
  cascadingFilters?: CascadingFilterRequest;
}

const AutocompleteMultiSelectFilter = ({
  value,
  onChange,
  isOpen,
  onOpen,
  autocompleteType,
  usesAutocompletetartOptions = false,
  label,
  searchPlaceholder,
  organizationId,
  cascadingFilters,
}: AutocompleteMultiSelectFilterProps) => {
  const { t } = useAdminTranslation();
  const [startOptions, setStartOptions] = useState<MultiSelectOption[]>([]);
  const [isStartOptionsLoading, setIsStartOptionsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || !organizationId || !autocompleteType || !usesAutocompletetartOptions) {
      return;
    }

    let isActive: boolean = true;

    const init = async () => {
      setIsStartOptionsLoading(true);
      try {
        let response: AutocompleteResponse<AutocompleteItem>;

        switch (autocompleteType) {
          case 'teachers':
            response = await autocompleteService.getTeachers(
              organizationId,
              undefined,
              cascadingFilters
            );
            break;
          case 'students':
            response = await autocompleteService.getStudents(
              organizationId,
              undefined,
              cascadingFilters
            );
            break;
          case 'groups':
            response = await autocompleteService.getGroups(
              organizationId,
              undefined,
              cascadingFilters
            );
            break;
          case 'class_code':
            response = await autocompleteService.getClassCodes(
              organizationId,
              undefined,
              cascadingFilters
            );
            break;
          default:
            return;
        }

        if (!isActive) {
          return;
        }

        if (response.status !== 'success' || !response.data) {
          setStartOptions([]);
          return;
        }

        const toOption = (item: AutocompleteItem): MultiSelectOption => {
          return autocompleteItemToMultiSelectOption(item, autocompleteType);
        };

        setStartOptions(response.data.map(toOption));
      } catch (error) {
        console.error('Autocomplete error:', error);
        setStartOptions([]);
      } finally {
        setIsStartOptionsLoading(false);
      }
    };

    init();

    return () => {
      isActive = false;
    };
  }, [isOpen, organizationId, autocompleteType, cascadingFilters, usesAutocompletetartOptions]);

  const selectedOptions = useMemo(() => {
    return value.map(i => autocompleteItemToMultiSelectOption(i, autocompleteType || 'groups'));
  }, [value, autocompleteType]);

  if (isOpen) {
    return (
      <div className={styles.filterMultiSelect}>
        {organizationId && autocompleteType ? (
          <MultiSelectDropDown
            Open
            variant='filter'
            placeholder={label}
            searchPlaceholder={searchPlaceholder || t('bimloaFilter.all') || ''}
            options={selectedOptions}
            value={selectedOptions}
            onChange={(opts: MultiSelectOption[]) => {
              onChange(opts.map(o => o.data.item));
            }}
            containerClassName={styles.filterDropdownContainer}
            headerClassName={styles.filterDropdownHeader}
            listClassName={styles.filterDropdownList}
            arrowClassName={styles.filterDropdownArrow}
            itemClassName={styles.filterDropdownItem}
            autocompleteType={autocompleteType}
            enableAutocomplete={true}
            organizationId={organizationId}
            autocompleteStartOptions={startOptions}
            cascadingFilters={cascadingFilters}
          />
        ) : (
          <></>
        )}
      </div>
    );
  }

  const text = (() => {
    if (value.length === 0) return t('bimloaFilter.all');
    return `${value.length} ${t('multiSelectDropDown.selected', 'selected')}`;
  })();

  return (
    <button className={styles.customSelect} onClick={() => onOpen()} type='button'>
      <span>{text}</span>
      <span className={styles.selectArrow}>
        <AppIcon icon='arrowDown' width={20} height={20} />
      </span>
    </button>
  );
};

export default AutocompleteMultiSelectFilter;
